export interface UsageTotals {
  query_count: number;
  generation_count: number;
  indexed_bytes: number;
  storage_bytes: number;
  user_count: number;
  corpus_count: number;
}

export interface CustomerUsage extends UsageTotals {
  customer_id: string;
}

export interface LineItem {
  metric: string;
  label: string;
  unit: string;
  included_quantity: number;
  used_quantity: number;
  used_raw: number;
  excess_quantity: number;
  overage_block_size: number;
  block_unit: string;
  block_rounding: BlockRounding;
  overage_blocks: number;
  overage_price: number;
  amount: number;
  source_text: string;
}

export interface BillingPeriodReport {
  period_start: string;
  period_end: string;
  partial_month: boolean;
  usage_by_customer: CustomerUsage[];
  totals: UsageTotals;
  line_items: LineItem[];
  overage_total: number;
}

export interface TermSummary {
  metric: string;
  label: string;
  unit: string;
  excess_quantity: number;
  amount: number;
}

export interface OverageReport {
  customer_ids: string[];
  from_date: string;
  to_date: string;
  currency: string;
  byte_unit_system: ByteUnitSystem;
  billing_periods: BillingPeriodReport[];
  summary_by_term: TermSummary[];
  grand_total_overage: number;
  assumptions: string[];
  warnings: string[];
}

export type BlockRounding = "up" | "down";

export interface ContractTerm {
  metric: string;
  label: string;
  included_quantity: number;
  included_unit: string;
  overage_price: number;
  overage_block_size: number;
  block_unit: string;
  block_rounding: BlockRounding;
  currency: string;
  source_text: string;
}

export interface AgentOutput {
  status: "complete" | "failed";
  contract_summary: string;
  contract_terms: ContractTerm[];
  unpriced_contract_items: string[];
  overage_summary: string;
  notes: string[];
  applied_lessons: string[];
  self_corrections: string[];
  lessons_learned: string[];
  failure_reason?: string | null;
}

export type ByteUnitSystem = "decimal" | "binary";

export interface ReportInputs {
  contractFileName: string;
  customerIds: string[];
  fromDate: string;
  toDate: string;
  byteUnitSystem: ByteUnitSystem;
}

export interface ReportResult {
  generatedAt: string;
  sessionKey: string;
  revision: number;
  reviewerFeedback: string[];
  inputs: ReportInputs;
  agentOutput: AgentOutput;
  report: OverageReport | null;
  verificationIssues: string[];
}

export function parseCustomerIds(raw: string): string[] {
  const ids = raw
    .split(/[\s,;]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  const invalid = ids.filter((id) => !/^[0-9]+$/.test(id));
  if (invalid.length > 0) {
    throw new Error(`Customer IDs must be numeric: ${invalid.join(", ")}`);
  }
  return [...new Set(ids)];
}

export function requestMessage(inputs: ReportInputs): string {
  return [
    "Produce the contract overage report.",
    `Contract file: ${inputs.contractFileName} (uploaded with this message)`,
    `Customer IDs: ${JSON.stringify(inputs.customerIds)}`,
    `From date: ${inputs.fromDate}`,
    `To date: ${inputs.toDate}`,
    `Byte unit system: ${inputs.byteUnitSystem}`
  ].join("\n");
}

const CONTRACT_EXTENSIONS = [".docx", ".pdf", ".txt"];

export function isSupportedContract(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return CONTRACT_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

function csvCell(value: string | number | boolean): string {
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvRows(rows: (string | number | boolean)[][]): string {
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}

export function lineItemsCsv(report: OverageReport): string {
  const header = [
    "period_start",
    "period_end",
    "partial_month",
    "term",
    "metric",
    "unit",
    "included_quantity",
    "used_quantity",
    "excess_quantity",
    "overage_block_size",
    "block_unit",
    "block_rounding",
    "overage_blocks",
    "overage_price",
    `amount_${report.currency}`
  ];
  const rows = report.billing_periods.flatMap((period) =>
    period.line_items.map((item) => [
      period.period_start,
      period.period_end,
      period.partial_month,
      item.label,
      item.metric,
      item.unit,
      item.included_quantity,
      item.used_quantity,
      item.excess_quantity,
      item.overage_block_size,
      item.block_unit,
      item.block_rounding,
      item.overage_blocks,
      item.overage_price,
      item.amount
    ])
  );
  return csvRows([header, ...rows]);
}

export function usageCsv(report: OverageReport): string {
  const header = [
    "period_start",
    "period_end",
    "customer_id",
    "query_count",
    "generation_count",
    "indexed_bytes",
    "storage_bytes",
    "user_count",
    "corpus_count"
  ];
  const rows = report.billing_periods.flatMap((period) =>
    period.usage_by_customer.map((usage) => [
      period.period_start,
      period.period_end,
      usage.customer_id,
      usage.query_count,
      usage.generation_count,
      usage.indexed_bytes,
      usage.storage_bytes,
      usage.user_count,
      usage.corpus_count
    ])
  );
  return csvRows([header, ...rows]);
}

export function feedbackMessage(feedback: string): string {
  return `Reviewer feedback on the report above: ${feedback.trim()}`;
}

export function sessionName(inputs: ReportInputs, startedAt: Date): string {
  return `Overage report ${inputs.contractFileName} ${inputs.fromDate}..${inputs.toDate} ${startedAt.toISOString()}`;
}

export function reportFileStem(result: ReportResult): string {
  return `overage-report_${result.inputs.fromDate}_${result.inputs.toDate}${
    result.revision > 1 ? `_rev${result.revision}` : ""
  }`;
}

export function quantityWithUnit(value: number, unit: string): string {
  return unit === "count" ? formatNumber(value) : `${formatNumber(value)} ${unit}`;
}

export function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

export function formatNumber(value: number, maximumFractionDigits = 3): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(value);
}

export function formatPeriod(period: Pick<BillingPeriodReport, "period_start" | "period_end">): string {
  return `${period.period_start.slice(0, 10)} → ${period.period_end.slice(0, 10)}`;
}
